import React, { useEffect, useMemo, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type { SeatInfo } from '../charter.types';

import {
  type BulkSeatOverrideFormValue,
  type BulkSeatOverrideResetValue,
  MANUAL_BLOCK_REASON_LABELS,
  type ManualBlockReason,
  type SeatOverrideFormValue,
  getBulkSeatOverrideFormValidation,
} from './CharterSeatConfigurationOverridesHelpers';

type Props = {
  open: boolean;
  seats: Array<SeatInfo>;
  baseSeat: SeatInfo | null;
  onClose: () => void;
  onSave: (value: SeatOverrideFormValue | BulkSeatOverrideFormValue | BulkSeatOverrideResetValue) => void;
  isSaving: boolean;
};

export default function CharterSeatOverrideDialog({
  open,
  seats,
  baseSeat,
  onClose,
  onSave,
  isSaving,
}: Props): React.JSX.Element {
  const seat = seats[0] ?? null;
  const isBulkEdit = seats.length > 1;
  const isOccupied = Boolean(seat?.isOccupied);
  const [isAvailable, setIsAvailable] = useState(true);
  const [blockReason, setBlockReason] = useState<ManualBlockReason | ''>('');
  const [price, setPrice] = useState('');
  const [applyAvailability, setApplyAvailability] = useState(false);
  const [applyPrice, setApplyPrice] = useState(false);

  useEffect(() => {
    if (!seat) {
      return;
    }

    setIsAvailable(seat.isAvailable);
    setBlockReason(seat.blockReason && seat.blockReason !== 'occupied' ? (seat.blockReason as ManualBlockReason) : '');
    setPrice(String(seat.price ?? 0));
    setApplyAvailability(false);
    setApplyPrice(false);
  }, [isBulkEdit, seat]);

  const parsedPrice = Number(price);
  const bulkValidation = getBulkSeatOverrideFormValidation({
    applyAvailability,
    isAvailable,
    blockReason: blockReason || undefined,
    applyPrice,
    price: parsedPrice,
  });
  const priceError = isBulkEdit ? bulkValidation.priceError : !Number.isFinite(parsedPrice) || parsedPrice < 0;
  const blockReasonError = isBulkEdit ? bulkValidation.blockReasonError : !isAvailable && !blockReason;
  const isSaveDisabled =
    !seat ||
    (!isBulkEdit && (!baseSeat || isOccupied)) ||
    (isBulkEdit && !bulkValidation.isValid) ||
    priceError ||
    blockReasonError ||
    isSaving;

  const statusText = useMemo(() => {
    if (isOccupied) {
      return 'Booked / occupied';
    }
    return seat?.isAvailable ? 'Available' : 'Blocked';
  }, [isOccupied, seat?.isAvailable]);

  const handleResetToBase = () => {
    if (isBulkEdit) {
      onSave({ resetToBase: true });
      return;
    }

    if (!baseSeat) {
      return;
    }

    setIsAvailable(baseSeat.isAvailable);
    setBlockReason(
      baseSeat.blockReason && baseSeat.blockReason !== 'occupied' ? (baseSeat.blockReason as ManualBlockReason) : '',
    );
    setPrice(String(baseSeat.price ?? 0));
  };

  const handleSave = () => {
    if (!seat || isSaveDisabled) {
      return;
    }

    if (isBulkEdit) {
      onSave({
        applyAvailability,
        isAvailable,
        blockReason: applyAvailability && !isAvailable ? (blockReason as ManualBlockReason) : undefined,
        applyPrice,
        price: parsedPrice,
      });
      return;
    }

    if (!baseSeat) {
      return;
    }

    onSave({
      isAvailable,
      blockReason: isAvailable ? undefined : (blockReason as ManualBlockReason),
      price: parsedPrice,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isBulkEdit
          ? `Edit ${seats.length} seats`
          : seat
            ? `Seat ${seat.displayLabel ?? seat.seatId}`
            : 'Seat override'}
      </DialogTitle>
      <DialogContent>
        {isBulkEdit ? (
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Alert severity="info">
              Bulk changes will be applied to selected non-booked seats only. Fields left unchecked keep each seat's
              current value.
            </Alert>

            <FormControlLabel
              control={
                <Checkbox
                  checked={applyAvailability}
                  onChange={(event) => setApplyAvailability(event.target.checked)}
                />
              }
              label="Apply block status"
            />

            <FormControl disabled={!applyAvailability}>
              <RadioGroup
                row
                value={isAvailable ? 'available' : 'blocked'}
                onChange={(_, value) => setIsAvailable(value === 'available')}
              >
                <FormControlLabel value="available" control={<Radio />} label="Unblocked" />
                <FormControlLabel value="blocked" control={<Radio />} label="Blocked" />
              </RadioGroup>
            </FormControl>

            {applyAvailability && !isAvailable ? (
              <FormControl required error={blockReasonError}>
                <InputLabel id="bulk-seat-block-reason-label">Block reason</InputLabel>
                <Select
                  labelId="bulk-seat-block-reason-label"
                  label="Block reason"
                  value={blockReason}
                  onChange={(event) => setBlockReason(event.target.value as ManualBlockReason)}
                >
                  {Object.entries(MANUAL_BLOCK_REASON_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
                {blockReasonError ? <FormHelperText>Block reason is required.</FormHelperText> : null}
              </FormControl>
            ) : null}

            <FormControlLabel
              control={<Checkbox checked={applyPrice} onChange={(event) => setApplyPrice(event.target.checked)} />}
              label="Apply seat price"
            />

            <TextField
              label="Seat price"
              value={price}
              type="number"
              disabled={!applyPrice}
              onChange={(event) => setPrice(event.target.value)}
              error={priceError}
              helperText={
                applyPrice ? (priceError ? 'Price must be a non-negative number.' : ' ') : 'Current prices are kept.'
              }
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Stack>
        ) : seat && baseSeat ? (
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                Current status
              </Typography>
              <Typography variant="subtitle1" fontWeight={600}>
                {statusText}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Base price ${baseSeat.price ?? 0}
              </Typography>
            </Stack>

            {isOccupied ? (
              <Alert severity="info">
                This seat is booked. Block status and seat price are managed by the existing seat selection.
              </Alert>
            ) : null}

            <FormControl disabled={isOccupied}>
              <RadioGroup
                row
                value={isAvailable ? 'available' : 'blocked'}
                onChange={(_, value) => setIsAvailable(value === 'available')}
              >
                <FormControlLabel value="available" control={<Radio />} label="Unblocked" />
                <FormControlLabel value="blocked" control={<Radio />} label="Blocked" />
              </RadioGroup>
            </FormControl>

            {!isAvailable ? (
              <FormControl required error={blockReasonError} disabled={isOccupied}>
                <InputLabel id="seat-block-reason-label">Block reason</InputLabel>
                <Select
                  labelId="seat-block-reason-label"
                  label="Block reason"
                  value={blockReason}
                  onChange={(event) => setBlockReason(event.target.value as ManualBlockReason)}
                >
                  {Object.entries(MANUAL_BLOCK_REASON_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
                {blockReasonError ? <FormHelperText>Block reason is required.</FormHelperText> : null}
              </FormControl>
            ) : null}

            <TextField
              label="Seat price"
              value={price}
              type="number"
              disabled={isOccupied}
              onChange={(event) => setPrice(event.target.value)}
              error={priceError}
              helperText={
                isOccupied
                  ? 'Booked seat prices cannot be changed.'
                  : priceError
                    ? 'Price must be a non-negative number.'
                    : ' '
              }
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleResetToBase} disabled={isBulkEdit ? isSaving : !baseSeat || isOccupied || isSaving}>
          {isBulkEdit ? 'Reset selected to base' : 'Reset to base'}
        </Button>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <LoadingButton variant="contained" onClick={handleSave} loading={isSaving} disabled={isSaveDisabled}>
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
